import type { BonusPg, Pessoa } from '@pgcustom/shared-types';
import { reaisParaCentavos } from '@pgcustom/shared-utils';
import { ApiError } from '../../core/api-client.js';
import { FORM_FIELD_CSS } from '../../core/form-field-styles.js';
import type { SocietarioApi } from '../societario/societario.api.js';

/**
 * `<erp-registrar-bonus-modal>` — registro manual de um Bônus PG. Todo
 * bônus nasce `proposto` (nunca aprovado neste passo) — o backend
 * (`societario.service.ts`) não aceita nenhum outro status inicial. Este
 * modal NUNCA calcula um valor sugerido: o campo "Valor" começa vazio e
 * quem decide o número é sempre a pessoa preenchendo o formulário (regra
 * discricionária do owner — "não implemente nenhuma fórmula automática
 * de geração de bônus").
 */
export class ErpRegistrarBonusModal extends HTMLElement {
  private readonly root: ShadowRoot;
  private _societarioApi: SocietarioApi | null = null;
  private _aoRegistrar: ((bonus: BonusPg) => void) | null = null;

  private aberto = false;
  private pessoas: Pessoa[] = [];
  private enviando = false;
  private mensagemErro = '';

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  set societarioApi(valor: SocietarioApi) {
    this._societarioApi = valor;
  }

  set aoRegistrar(valor: (bonus: BonusPg) => void) {
    this._aoRegistrar = valor;
  }

  abrir(pessoas: Pessoa[]): void {
    this.pessoas = pessoas;
    this.mensagemErro = '';
    this.aberto = true;
    this.render();
  }

  connectedCallback(): void {
    this.render();
  }

  private fechar(): void {
    if (this.enviando) return;
    this.aberto = false;
    this.render();
  }

  private async aoSubmeter(evento: Event): Promise<void> {
    evento.preventDefault();
    if (!this._societarioApi || this.enviando) return;

    const pessoaId = this.valorCampo('#pessoa');
    const competencia = this.valorCampo('#competencia');
    const valorReais = Number(this.valorCampo('#valor').replace(',', '.'));
    const motivo = this.valorCampo('#motivo').trim();
    const observacao = this.valorCampo('#observacao').trim();

    if (!pessoaId) {
      this.mensagemErro = 'selecione a pessoa beneficiária';
      this.render();
      return;
    }
    if (!competencia) {
      this.mensagemErro = 'selecione a competência (mês/ano)';
      this.render();
      return;
    }
    if (!Number.isFinite(valorReais) || valorReais <= 0) {
      this.mensagemErro = 'valor deve ser maior que zero';
      this.render();
      return;
    }
    if (!motivo) {
      this.mensagemErro = 'motivo é obrigatório — todo bônus precisa de uma justificativa registrada';
      this.render();
      return;
    }

    this.enviando = true;
    this.mensagemErro = '';
    this.render();

    try {
      const bonus = await this._societarioApi.registrarBonus({
        pessoaId,
        competencia,
        valorCentavos: reaisParaCentavos(valorReais),
        motivo,
        observacao: observacao || undefined,
      });
      this.aberto = false;
      this._aoRegistrar?.(bonus);
    } catch (erro) {
      this.mensagemErro = erro instanceof ApiError ? erro.message : 'não foi possível registrar. tente novamente.';
    } finally {
      this.enviando = false;
      this.render();
    }
  }

  private valorCampo(seletor: string): string {
    return this.root.querySelector<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(seletor)?.value ?? '';
  }

  private render(): void {
    this.root.innerHTML = `
      <style>
        :host { display: block; }
        form { display: flex; flex-direction: column; gap: var(--pg-space-4, 16px); font-family: var(--pg-font-familia-base, system-ui); }
        ${FORM_FIELD_CSS}
        textarea { resize: vertical; min-height: 64px; }
        .rodape { display: flex; justify-content: flex-end; gap: var(--pg-space-3, 12px); }
      </style>
      <pg-modal id="modal" ${this.aberto ? 'open' : ''}>
        <span slot="header">Registrar Bônus PG</span>
        <form novalidate>
          <label>
            Pessoa
            <select id="pessoa">
              <option value="">selecione…</option>
              ${this.pessoas.map((p) => `<option value="${p.id}">${escaparHtml(p.nome)}</option>`).join('')}
            </select>
          </label>
          <label>
            Competência
            <input id="competencia" type="month" />
          </label>
          <label>
            Valor (R$)
            <input id="valor" type="text" inputmode="decimal" placeholder="0,00" />
          </label>
          <label>
            Motivo
            <input id="motivo" type="text" maxlength="255" placeholder="ex: reconhecimento por resultado excepcional no mês" />
          </label>
          <label>
            Observação (opcional)
            <textarea id="observacao" maxlength="1000"></textarea>
          </label>
          <span class="dica">Este bônus nasce como "proposto" — ainda precisa ser aprovado e pago em etapas separadas.</span>
          <span class="erro">${escaparHtml(this.mensagemErro)}</span>
        </form>
        <div slot="footer" class="rodape">
          <pg-button id="botao-cancelar" variant="ghost">Cancelar</pg-button>
          <pg-button id="botao-registrar" variant="primary" ${this.enviando ? 'loading' : ''}>Registrar</pg-button>
        </div>
      </pg-modal>
    `;

    const form = this.root.querySelector('form');
    form?.addEventListener('submit', (evento) => this.aoSubmeter(evento));
    this.root.querySelector('#botao-registrar')?.addEventListener('pg-click', (evento) => this.aoSubmeter(evento));
    this.root.querySelector('#botao-cancelar')?.addEventListener('pg-click', () => this.fechar());
    this.root.querySelector('#modal')?.addEventListener('pg-close', () => this.fechar());
  }
}

function escaparHtml(valor: string): string {
  const div = document.createElement('div');
  div.textContent = valor;
  return div.innerHTML;
}

customElements.define('erp-registrar-bonus-modal', ErpRegistrarBonusModal);

declare global {
  interface HTMLElementTagNameMap {
    'erp-registrar-bonus-modal': ErpRegistrarBonusModal;
  }
}
